import { supabase } from "@/integrations/supabase/client";

/**
 * Safely upsert a display year record, handling the unique constraint on (display_id, year)
 * @param displayId - The display ID
 * @param year - The year (defaults to current year)
 * @param description - Optional description for the year
 * @returns The display year record or null if error
 */
export const upsertDisplayYear = async (
  displayId: number,
  year?: number,
  description?: string
) => {
  const targetYear = year || new Date().getFullYear();
  const yearDescription = description || `${targetYear} Show`;

  try {
    // First, try to find existing record
    const { data: existingYear, error: fetchError } = await supabase
      .from('display_years')
      .select('*')
      .eq('display_id', displayId)
      .eq('year', targetYear)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 is "not found" error, which is fine
      console.error('Error fetching display year:', fetchError);
      throw fetchError;
    }

    if (existingYear) {
      // Update existing record if description is different
      if (existingYear.description !== yearDescription) {
        const { data: updatedYear, error: updateError } = await supabase
          .from('display_years')
          .update({ description: yearDescription })
          .eq('id', existingYear.id)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating display year:', updateError);
          throw updateError;
        }

        return updatedYear;
      }
      return existingYear;
    } else {
      // Create new record
      const { data: newYear, error: insertError } = await supabase
        .from('display_years')
        .insert({
          display_id: displayId,
          year: targetYear,
          description: yearDescription
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating display year:', insertError);
        throw insertError;
      }

      return newYear;
    }
  } catch (error) {
    console.error('Error in upsertDisplayYear:', error);
    throw error;
  }
};

/**
 * Safely upsert a user collection year (display_year with null display_id)
 * @param userId - The user ID
 * @param year - The year (defaults to current year)
 * @param description - Optional description for the year
 * @returns The display year record or null if error
 */
export const upsertUserCollectionYear = async (
  userId: string,
  year?: number,
  description?: string
) => {
  const targetYear = year || new Date().getFullYear();
  const yearDescription = description || `Personal song collection for ${targetYear}`;

  try {
    // First, try to find existing record
    const { data: existingYear, error: fetchError } = await supabase
      .from('display_years')
      .select('*')
      .is('display_id', null)
      .eq('user_id', userId)
      .eq('year', targetYear)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 is "not found" error, which is fine
      console.error('Error fetching user collection year:', fetchError);
      throw fetchError;
    }

    if (existingYear) {
      return existingYear;
    } else {
      // Create new record
      const { data: newYear, error: insertError } = await supabase
        .from('display_years')
        .insert({
          display_id: null,
          user_id: userId,
          year: targetYear,
          description: yearDescription
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating user collection year:', insertError);
        throw insertError;
      }

      return newYear;
    }
  } catch (error) {
    console.error('Error in upsertUserCollectionYear:', error);
    throw error;
  }
};